import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconTileChoice, type IconTileOption } from '../../../../../../components/design-system/common';
import type { SurfaceFinish } from '../../../../../../common/models/product-v2.model';
import { Card } from './Card';

interface FinishCardV3Props {
  /** The product's finish; absent means matte. */
  value?: SurfaceFinish;
  onChange: (finish: SurfaceFinish) => void;
}

const Glyph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);

const ICONS: Record<SurfaceFinish, React.ReactNode> = {
  matte: <Glyph><rect x="10" y="10" width="28" height="28" rx="3" /></Glyph>,
  satin: (
    <Glyph>
      <rect x="10" y="10" width="28" height="28" rx="3" />
      <path d="M17 29l12-12" strokeWidth="5" strokeOpacity="0.3" />
    </Glyph>
  ),
  glossy: (
    <Glyph>
      <rect x="10" y="10" width="28" height="28" rx="3" />
      <path d="M15 24l9-9M19 31l12-12" />
    </Glyph>
  ),
  lacquered: (
    <Glyph>
      <rect x="8" y="11" width="32" height="7" rx="1.5" />
      <rect x="8" y="20.5" width="32" height="7" rx="1.5" />
      <rect x="8" y="30" width="32" height="7" rx="1.5" />
      <path d="M14 24h10" />
    </Glyph>
  ),
  handmade: (
    <Glyph>
      <path d="M10 11.5q6.5-1.5 13 0 1.2 6-.2 11.5-6.3 1.2-12.8-.2-1.2-5.6 0-11.3z" />
      <path d="M25.2 11.3q6.3-1.2 12.8.2 1.1 5.8-.2 11.3-6.5 1.3-12.8-.1-1-5.8.2-11.4z" />
      <path d="M10.2 25.4q6.4-1.3 12.7.1 1.2 5.7 0 11.2-6.4 1.4-12.8 0-1.2-5.6.1-11.3z" />
      <path d="M25.3 25.3q6.4 1.1 12.8 0 1.1 5.9-.2 11.3-6.3 1.3-12.7-.1-1.1-5.6.1-11.2z" />
      <path d="M14 15.5l3-3M29 29.5l3-3" />
    </Glyph>
  ),
};

/**
 * Surface products: how the product reflects light. For any finish but matte,
 * shoppers can turn reflections on in the visualizer; they start off. Shown
 * only to vendors Ardealy has switched finishes on for (finishesEnabled).
 */
export const FinishCardV3: React.FC<FinishCardV3Props> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const options: IconTileOption<SurfaceFinish>[] = [
    {
      value: 'matte',
      label: t('PAGES.NEW_PRODUCT.V3.FINISH.MATTE', 'Matte'),
      hint: t('PAGES.NEW_PRODUCT.V3.FINISH.MATTE_HINT', 'No reflections, so shoppers are not offered them.'),
      icon: ICONS.matte,
    },
    {
      value: 'satin',
      label: t('PAGES.NEW_PRODUCT.V3.FINISH.SATIN', 'Satin'),
      hint: t('PAGES.NEW_PRODUCT.V3.FINISH.SATIN_HINT', 'A soft, blurred sheen.'),
      icon: ICONS.satin,
    },
    {
      value: 'glossy',
      label: t('PAGES.NEW_PRODUCT.V3.FINISH.GLOSSY', 'Glossy / polished'),
      hint: t('PAGES.NEW_PRODUCT.V3.FINISH.GLOSSY_HINT', 'Sharp reflections, like polished porcelain or a glazed wall tile.'),
      icon: ICONS.glossy,
    },
    {
      value: 'lacquered',
      label: t('PAGES.NEW_PRODUCT.V3.FINISH.LACQUERED', 'Lacquered'),
      hint: t('PAGES.NEW_PRODUCT.V3.FINISH.LACQUERED_HINT', 'Varnished or lacquered wood.'),
      icon: ICONS.lacquered,
    },
    {
      value: 'handmade',
      label: t('PAGES.NEW_PRODUCT.V3.FINISH.HANDMADE', 'Handmade'),
      hint: t('PAGES.NEW_PRODUCT.V3.FINISH.HANDMADE_HINT', 'Zellige and handmade glaze: each tile catches the light its own way.'),
      icon: ICONS.handmade,
    },
  ];

  return (
    <Card title={t('PAGES.NEW_PRODUCT.V3.FINISH.TITLE', 'Finish')} testId="product-finish-card">
      <p className="mb-3 text-sm text-muted-foreground">
        {t(
          'PAGES.NEW_PRODUCT.V3.FINISH.DESC',
          'How this product reflects light. In the visualizer, shoppers can turn reflections on for any finish but matte.'
        )}
      </p>
      <IconTileChoice value={value ?? 'matte'} options={options} onChange={onChange} columns={5} />
    </Card>
  );
};
